import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { RulesApiService } from 'src/app/core/services/rules-api.service';
import { DeleteConfirmModalComponent } from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import { EntityCatalogComponent } from 'src/app/shared/components/entity-catalog/entity-catalog.component';
import { entityText } from 'src/app/shared/components/entity-catalog/entity-text.util';
import {
  buildDeletedCatalog,
  formatDeletedAt,
} from 'src/app/shared/components/entity-catalog/entity-catalog-deleted.util';
import { EntityCatalog, EntityRecord } from 'src/app/shared/components/entity-catalog/entity-catalog.types';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import { toSelectableCountryPackOptions } from '../country-packs/country-pack-options';
import { COMPLIANCE_LOCK_CATALOG } from './compliance-locks.catalog';

type ComplianceLockView = 'active' | 'deleted';

function buildActiveCatalog(
  countryPackOptions: ReturnType<typeof toSelectableCountryPackOptions>,
): EntityCatalog {
  return {
    ...COMPLIANCE_LOCK_CATALOG,
    fields: COMPLIANCE_LOCK_CATALOG.fields.map((field) =>
      field.key === 'country_code' ? { ...field, options: countryPackOptions } : field,
    ),
  };
}

@Component({
  selector: 'app-compliance-locks',
  standalone: true,
  imports: [
    CommonModule,
    EntityCatalogComponent,
    PageSurfaceHeaderComponent,
    DeleteConfirmModalComponent,
    RestoreConfirmModalComponent,
  ],
  templateUrl: './compliance-locks.component.html',
})
export class ComplianceLocksComponent implements OnInit {
  private readonly api = inject(RulesApiService);

  activeCatalog: EntityCatalog = COMPLIANCE_LOCK_CATALOG;
  readonly deletedCatalog = buildDeletedCatalog(COMPLIANCE_LOCK_CATALOG);
  readonly toolbarTabs = [{ key: 'deleted', label: 'Deleted' }];

  view: ComplianceLockView = 'active';
  records: EntityRecord[] = [];
  loading = false;
  saving = false;
  error = '';

  showDeleteModal = false;
  deleteError = '';
  readonly deleteLoading = signal(false);
  selectedForDelete: EntityRecord | null = null;

  showRestoreModal = false;
  readonly restoreLoading = signal(false);
  selectedForRestore: EntityRecord | null = null;

  ngOnInit(): void {
    this.loadCountryPacks();
    this.load();
  }

  get catalogView(): EntityCatalog {
    return this.view === 'deleted' ? this.deletedCatalog : this.activeCatalog;
  }

  get activeToolbarTab(): string {
    return this.view === 'deleted' ? 'deleted' : '';
  }

  onToolbarTabChange(key: string): void {
    this.view = this.view === key ? 'active' : (key as ComplianceLockView);
    this.error = '';
    this.load();
  }

  private loadCountryPacks(): void {
    this.api
      .listCountryPackRecords()
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (res) => {
          this.activeCatalog = buildActiveCatalog(toSelectableCountryPackOptions(res.data ?? []));
        },
        error: () => {
          this.activeCatalog = buildActiveCatalog([]);
        },
      });
  }

  load(): void {
    this.loading = true;
    this.error = '';
    const request$ =
      this.view === 'deleted'
        ? this.api.listDeletedComplianceLockRecords()
        : this.api.listComplianceLockRecords();

    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          const rows = (res.data ?? []) as EntityRecord[];
          this.records =
            this.view === 'deleted'
              ? rows.map((row) => ({ ...row, deleted_at: formatDeletedAt(row['deleted_at']) }))
              : rows;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onSave(event: { record: EntityRecord; isNew: boolean }): void {
    this.saving = true;
    this.error = '';
    this.api
      .upsertComplianceLock(this.toRequestBody(event.record))
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: () => this.load(),
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onDelete(row: EntityRecord): void {
    this.deleteError = '';
    this.selectedForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedForDelete;
    if (!row) return;

    const id = Number(row['id']);
    if (!Number.isFinite(id) || id <= 0) return;

    this.deleteLoading.set(true);
    this.deleteError = '';
    this.api
      .deleteComplianceLock(id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.deleteLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.load();
        },
        error: (err) => (this.deleteError = resolveApiErrorMessage(err)),
      });
  }

  onRestore(row: EntityRecord): void {
    this.selectedForRestore = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedForRestore = null;
  }

  submitRestore(): void {
    const row = this.selectedForRestore;
    if (!row) return;

    const id = Number(row['id']);
    if (!Number.isFinite(id) || id <= 0) return;

    this.restoreLoading.set(true);
    this.error = '';
    this.api
      .restoreComplianceLock(id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.restoreLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeRestoreModal();
          this.view = 'active';
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  get restoreEntityName(): string {
    return this.entityLabel(this.selectedForRestore);
  }

  get deleteEntityName(): string {
    return this.entityLabel(this.selectedForDelete);
  }

  private entityLabel(row: EntityRecord | null): string {
    if (!row) return '';
    const country = entityText(row['country_code']).trim();
    const parameter = entityText(row['parameter_key']).trim();
    return parameter ? `${country} · ${parameter}` : country;
  }

  private toRequestBody(record: EntityRecord): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    for (const field of this.activeCatalog.fields) {
      const value = record[field.key];
      if (value === '' || value == null) continue;
      body[field.key] = value;
    }
    return body;
  }
}
